import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { Card, Typography, Row, Col, Tabs, Button, Divider, Alert } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ThunderboltOutlined, 
  RocketOutlined, 
  CoffeeOutlined, 
  CompassOutlined 
} from '@ant-design/icons';

// Import your form components
import SeabobContractForm from '../ContractsGenerator/SeabobContractForm';
// Uncomment these when they're available
// import JetskiContractForm from '../ContractsGenerator/JetskiContractForm';
// import LunchContractForm from '../ContractsGenerator/LunchContractForm';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const FormSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeForm, setActiveForm] = useState(null);

  // Parse the URL on initial load to determine active form
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    if (type) {
      setActiveForm(type);
    }
  }, [location]);

  // Update URL when active form changes
  useEffect(() => {
    if (activeForm) {
      navigate(`/form-selection?type=${activeForm}`, { replace: true });
    } else {
      navigate('/form-selection', { replace: true });
    }
  }, [activeForm, navigate]);

  const formOptions = [
    {
      key: 'seabob',
      title: 'Seabob',
      description: 'Contrat de location pour Seabobs',
      icon: <ThunderboltOutlined style={{ fontSize: 48 }} />,
      color: theme.colors.white,
    },
    {
      key: 'jetski',
      title: 'Jetski',
      description: 'Contrat de location pour Jetskis',
      icon: <RocketOutlined style={{ fontSize: 48 }} />,
      color: theme.colors.textSecondary,
    },
    {
      key: 'lunch',
      title: 'Lunch',
      description: 'Contrat pour service de lunch',
      icon: <CoffeeOutlined style={{ fontSize: 48 }} />,
      color: theme.colors.white,
    },
    {
      key: 'yacht',
      title: 'Yacht',
      description: 'Contrat pour location de yacht',
      icon: <CompassOutlined style={{ fontSize: 48 }} />,
      color: theme.colors.textSecondary,
    }
  ];

  // Render the selected form
  const renderForm = () => {
    switch (activeForm) {
      case 'seabob':
        return <SeabobContractForm />;
      case 'jetski':
        // Replace with actual component when available
        return (
          <Alert
            message="Formulaire Jetski"
            description="Le formulaire Jetski sera disponible prochainement."
            type="info"
            showIcon
          />
        );
      case 'lunch':
        // Replace with actual component when available
        return (
          <Alert
            message="Formulaire Lunch"
            description="Le formulaire Lunch sera disponible prochainement."
            type="info"
            showIcon
          />
        );
      case 'yacht':
        // Redirect to the yacht contract page
        navigate('/yacht-contract');
        return null;
      default:
        return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark,  textAlign: 'center', padding: '50px 0' }}>
            <Title level={3}>Sélectionnez un type de formulaire ci-dessus</Title>
            <Paragraph style={{ fontSize: '16px' }}>
              Choisissez le type de contrat que vous souhaitez générer parmi les options disponibles.
            </Paragraph>
          </div>
        );
    }
  };

  // Get the title for the active form
  const getFormTitle = () => {
    const selectedForm = formOptions.find(option => option.key === activeForm);
    return selectedForm ? `Formulaire: ${selectedForm.title}` : 'Sélection de Formulaire';
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Génération de Contrats</Title>
      <Paragraph>
        Sélectionnez le type de contrat que vous souhaitez générer:
      </Paragraph>
      
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {formOptions.map((option) => (
          <Col xs={24} sm={12} md={6} key={option.key}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                textAlign: 'center',
                borderTop: `4px solid ${option.color}`,
                borderColor: activeForm === option.key ? option.color : '#f0f0f0',
                borderRadius: '8px',
                boxShadow: activeForm === option.key ? `0 0 10px ${option.color}40` : '0 2px 8px rgba(0,0,0,0.1)',
                transform: activeForm === option.key ? 'translateY(-4px)' : 'none',
                transition: 'all 0.3s ease'
              }}
              onClick={() => setActiveForm(option.key)}
            >
              <div style={{ 
                marginBottom: '16px', 
                color: option.color
              }}>
                {option.icon}
              </div>
              <Title level={4} style={{ margin: '12px 0' }}>
                {option.title}
              </Title>
              <Paragraph style={{ color: '#666' }}>
                {option.description}
              </Paragraph>
              <Button 
                type={activeForm === option.key ? "primary" : "default"}
                style={{ 
                  marginTop: '12px',
                  ...(activeForm === option.key ? {
                    backgroundColor: option.color,
                    borderColor: option.color
                  } : {})
                }}
                onClick={() => setActiveForm(option.key)}
              >
                {activeForm === option.key ? 'Sélectionné' : 'Sélectionner'}
              </Button>
            </Card>
          </Col>
        ))}
      </Row>

      {activeForm && (
        <>
          <Divider>
            <Title level={3}>{getFormTitle()}</Title>
          </Divider>
          
          <div style={{ marginTop: '24px' }}>
            {renderForm()}
          </div>
        </>
      )}

      <div style={{ marginTop: '40px', textAlign: 'center' }}>
        <Button onClick={() => navigate('/contracts-summary')}>
          Retour au résumé des contrats
        </Button>
      </div>
    </div>
  );
};

export default FormSelectionPage;